import { parseUkPhoneNumber, validateUkPhoneNumber } from '@application/form_validators';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { InputText } from '@cellula/react';
import { InformationGatheringForm } from './information-gathering-form';
import { useInformationGatheringJourney } from './use-information-gathering-journey';

interface PhoneFormData {
	phoneNumber: string;
}

type PhoneSubject = 'self' | 'representative' | 'patient';

const PHONE_SCREEN_COPY: Record<
	PhoneSubject,
	{ title: I18nAndL10n.LocaleKey; description: I18nAndL10n.LocaleKey; label: I18nAndL10n.LocaleKey }
> = {
	self: {
		title: 'View.CreateNewMessage.InformationGathering.SelfContact.Title',
		description: 'View.CreateNewMessage.InformationGathering.SelfContact.Description',
		label: 'View.CreateNewMessage.InformationGathering.SelfContact.PhoneNumber',
	},
	representative: {
		title: 'View.CreateNewMessage.InformationGathering.RepresentativeContact.Title',
		description: 'View.CreateNewMessage.InformationGathering.RepresentativeContact.Description',
		label: 'View.CreateNewMessage.InformationGathering.RepresentativeContact.PhoneNumber',
	},
	patient: {
		title: 'View.CreateNewMessage.InformationGathering.PatientContact.Title',
		description: 'View.CreateNewMessage.InformationGathering.PatientContact.Description',
		label: 'View.CreateNewMessage.InformationGathering.PatientContact.PhoneNumber',
	},
};

export function PhoneScreen({ subject }: { subject: PhoneSubject }) {
	const { draft, saveDraft, goNext, goBack } = useInformationGatheringJourney();
	const representative = subject === 'representative';
	const copy = PHONE_SCREEN_COPY[subject];
	const phoneNumber = representative ? draft.representative.phoneNumber : draft.patient.phoneNumber;

	function submit(data: PhoneFormData) {
		const phoneNumber = parseUkPhoneNumber(data.phoneNumber);
		if (phoneNumber === null) return;

		saveDraft(
			representative
				? { ...draft, representative: { ...draft.representative, phoneNumber } }
				: { ...draft, patient: { ...draft.patient, phoneNumber } },
		);
		goNext();
	}

	return (
		<InformationGatheringForm
			title={copy.title}
			description={copy.description}
			defaultValues={{ phoneNumber }}
			onSubmit={submit}
			goBack={goBack}
		>
			<InputText
				autoComplete={subject === 'self' || representative ? 'tel' : undefined}
				inputMode='tel'
				type='tel'
				label={withTranslation(copy.label)}
				name='phoneNumber'
				required
				options={{
					required: I18nAndL10n.translate('Form.ValidationError.Required'),
					validate: {
						phoneNumber: (value: string) =>
							validateUkPhoneNumber(value) ||
							I18nAndL10n.translate('View.CreateNewMessage.InformationGathering.PhoneNumber.ValidationError.Format'),
					},
				}}
			/>
		</InformationGatheringForm>
	);
}
